import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["lb", "oz", "kg", "g", "count", "bunch"];

export default function YieldChart() {
  const [items, setItems] = useState([]);
  const [plants, setPlants] = useState([]);
  const [form, setForm] = useState({ plant_id: "", amount: "", unit: "lb", date: new Date().toISOString().slice(0,10) });
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/yield"), api.get("/garden")])
      .then(([y, g]) => { setItems(y.data); setPlants(g.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const byPlant = useMemo(() => {
    const m = new Map();
    for (const r of items) {
      const key = r.plant_name || "Unknown";
      m.set(key, (m.get(key) || 0) + Number(r.amount || 0));
    }
    return Array.from(m, ([name, total]) => ({ name, total: Math.round(total * 100) / 100 })).sort((a,b) => b.total - a.total);
  }, [items]);

  const byMonth = useMemo(() => {
    const m = new Map();
    for (const r of items) {
      const mo = (r.date || "").slice(0, 7);
      if (!mo) continue;
      m.set(mo, (m.get(mo) || 0) + Number(r.amount || 0));
    }
    return Array.from(m, ([name, total]) => ({ name, total: Math.round(total * 100) / 100 })).sort((a,b) => a.name.localeCompare(b.name));
  }, [items]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.plant_id || !form.amount) return toast.error("Pick a plant and amount.");
    try {
      await api.post("/yield", { ...form, amount: parseFloat(form.amount) });
      setForm({ ...form, amount: "" });
      refresh();
      toast.success("Harvest logged.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/yield/${id}`);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="yield-chart">
      <div className="sage-card p-6">
        <div className="label-overline mb-2">Log a harvest</div>
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Select value={form.plant_id} onValueChange={(v) => setForm({ ...form, plant_id: v })}>
            <SelectTrigger className="md:col-span-2" data-testid="yield-plant"><SelectValue placeholder="Choose plant" /></SelectTrigger>
            <SelectContent>
              {plants.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Add plants first</div>}
              {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="yield-amount" />
          <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
            <SelectTrigger data-testid="yield-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="yield-date" />
          <Button type="submit" className="bg-primary hover:bg-primary/90 md:col-span-5" data-testid="yield-add">Log harvest</Button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="sage-card p-6">
          <div className="label-overline mb-1">By plant</div>
          <h3 className="font-serif text-xl font-light mb-3">Totals harvested</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={byPlant} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e3dccd" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="hsl(131 22% 29%)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="sage-card p-6">
          <div className="label-overline mb-1">Over time</div>
          <h3 className="font-serif text-xl font-light mb-3">Monthly rhythm</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={byMonth} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e3dccd" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="hsl(32 50% 64%)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="sage-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-overline">Recent harvests</div>
            <h3 className="font-serif text-xl font-light">Log</h3>
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">No harvests yet. Log your first above.</div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`yield-row-${r.id}`}>
                <div className="flex-1">
                  <span className="font-medium">{r.plant_name}</span>
                  <span className="text-muted-foreground"> — {r.amount} {r.unit}</span>
                  {r.notes && <div className="text-xs text-muted-foreground mt-0.5">{r.notes}</div>}
                </div>
                <span className="text-xs text-muted-foreground">{r.date}</span>
                <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`yield-del-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
